import * as React from "react";
import {connect} from "react-redux";
import {none, option, Option} from "ts-option";
import {Dispatch} from "redux";
import Job from "../../../../model/job/Job";
import {editJob} from "../../../../store/action/job/job";
import DictionaryItem from "../../../../model/dictionary/DictionaryItem";
import Toast from "../../../helper/Toast";
import {isDefined, notifyBugsnagAndShowToast} from "../../../../utils/Utils";
import MerchantBranchLocation from "../../../../model/merchant/MerchantBranchLocation";
import {api} from "../../../../api/api";
import InfoModule from "../../../../hiberUiKit/src/molecules/InfoModule/InfoModule";
import {Icon} from "../../../../hiberUiKit/src/atoms/Icon/Icon";
import DropDownWrapper from "../../../../hiberUiKit/src/molecules/DropDownWrapper/DropDownWrapper";
import DropDownList, {DropDownItem} from "../../../../hiberUiKit/src/molecules/DropDownList/DropDownList";
import SearchDropDown from "../../../../hiberUiKit/src/molecules/SearchDropDown/SearchDropDown";
import Loading from "../../../ui/loading/Loading";
import {RootState} from "../../../../store/reducer/root/RootState";

interface ComponentProps {
	/**
	 * Job.
	 */
	job: Option<Job>
}

interface DispatchProps {
	/**
	 * Edit job action.
	 * @param {Job} job
	 */
	editJob: (job: Job) => void;
}

interface ReduxProps {
	/**
	 * Indicates if edition is disabled
	 */
	disabled: boolean
}

interface State {
	merchantBranchLocations: Option<MerchantBranchLocation[]>,
	menuDropDownVisible: boolean,
	searchBoxVisible: boolean
}

type Props = ComponentProps & DispatchProps & ReduxProps

class SelectJobMerchantBranchLocation extends React.PureComponent<Props, State> {
	selectedMerchantBranchLocationMenu: DropDownItem[] = [{id: "l1", label: "Change"}, {id: "l2", label: "Remove"}];
	emptyMerchantBranchLocationMenu: DropDownItem[] = [{id: "l1", label: "Select"}];

	constructor(props: Props) {
		super(props);
		this.state = {
			merchantBranchLocations: none,
			menuDropDownVisible: false,
			searchBoxVisible: false,
		};
	}

	componentDidMount() {
		const merchantId = this.props.job.flatMap(j => j.data.merchantId);
		this.fetchMerchantBranchLocations(merchantId);
	}

	componentDidUpdate(prevProps: Readonly<Props>) {
		const merchantId = this.props.job.flatMap(j => j.data.merchantId);
		if (prevProps.job.flatMap(j => j.data.merchantId).orNull !== merchantId.orNull) {
			this.fetchMerchantBranchLocations(merchantId);
		}
	}

	fetchMerchantBranchLocations = (merchantId: Option<string>) => {
		if (merchantId.isDefined) {
			api.instance.merchantApi.fetchMerchantBranchLocations(merchantId.get)
				.then(merchantBranchLocations => this.setState({merchantBranchLocations: option(merchantBranchLocations)}))
				.catch((err) => notifyBugsnagAndShowToast(err, "Fetch merchant branch locations"))
		}
		else {
			this.setState({merchantBranchLocations: option([])});
		}
	};

	onSelect = (merchantBranchLocation: DictionaryItem) => {
		this.setState({searchBoxVisible: false, menuDropDownVisible: false});
		this.props.job.map(j => {
			const updatedJob = j.copy({data: j.data.copy({merchantBranchLocationId: option(merchantBranchLocation.id)})});
			this.props.editJob(updatedJob);
		})
	};

	onClearClick = () => {
		this.props.job.map(j => {
			const updatedJob = j.copy({data: j.data.copy({merchantBranchLocationId: none})});
			this.props.editJob(updatedJob);
		})
	};

	render() {
		const selectedMerchantBranchLocationId = this.props.job.flatMap(j => j.data.merchantBranchLocationId).orNull;
		const [selectedMerchantBranchLocation] = this.state.merchantBranchLocations.getOrElse([]).filter(
			m => m.id == selectedMerchantBranchLocationId)
		const menuItems = selectedMerchantBranchLocationId ? this.selectedMerchantBranchLocationMenu :
			this.emptyMerchantBranchLocationMenu;
		return (
			<InfoModule icon={Icon.Job}
						title={"MERCHANT BRANCH"}
						actionIcon={Icon.Overflow}
						onActionIconClick={this.props.disabled ? undefined : this.toggleMenu}>
				{this.state.menuDropDownVisible &&
				<DropDownWrapper onOutsideClick={() => {this.setState({menuDropDownVisible: false})}}>
					<DropDownList items={menuItems}
								  itemToId={(link: DropDownItem) => link.id}
								  itemToLabel={(link: DropDownItem) => link.label}
								  itemDisabled={(link: DropDownItem) => link.disabled}
								  onSelect={this.onMenuItemSelect}/>
				</DropDownWrapper>}
				{this.state.searchBoxVisible &&
				<DropDownWrapper onOutsideClick={() => this.setState({searchBoxVisible: false})}>
					<SearchDropDown items={this.state.merchantBranchLocations.getOrElse([])}
									onChange={this.onSelect}
									itemToLabel={(item: MerchantBranchLocation) => item.name}
									itemToId={(item: MerchantBranchLocation) => item.id}
									placeholder={"Select branch"}
									selectedItemId={selectedMerchantBranchLocationId}
									isLoading={this.state.merchantBranchLocations.isEmpty}/>
				</DropDownWrapper>
				}
				<Loading isLoading={selectedMerchantBranchLocationId && !selectedMerchantBranchLocation} spinnerSize={24}>
					<span className={"t-body"}> {selectedMerchantBranchLocation ? selectedMerchantBranchLocation.name :
						"N/A"}</span>
				</Loading>

			</InfoModule>
		);
	}

	toggleMenu = () => {
		this.setState({menuDropDownVisible: !this.state.menuDropDownVisible, searchBoxVisible: false})
	}

	onMenuItemSelect = (item: DropDownItem) => {
		this.setState({menuDropDownVisible: false, searchBoxVisible: false});
		switch (item.id) {
			case "l1":
				this.setState({searchBoxVisible: true, menuDropDownVisible: false});
				break;
			case "l2":
				this.setState({menuDropDownVisible: false, searchBoxVisible: false}, this.onClearClick);
				break;
		}
	}
}

function mapDispatchToProps(dispatch: Dispatch<any>) {
	return {
		editJob: (job: Job) => dispatch(
			editJob(
				job,
				() => Toast.success("Merchant branch location updated", "Job details update"),
				(err) => notifyBugsnagAndShowToast(err, "Job details update"))
		),
	}
}

function mapStateToProps(state: RootState, props: ComponentProps) {
	const jobId = props.job.map(j => j.id).orNull
	const jobType = props.job.flatMap(
		j => option(state.jobType.jobTypes.getOrElse([]).filter(jt => jt.id == j.data.jobTypeId)[0]))
	const disabled = isDefined(state.billOfMaterials.bomMap[jobId]) || jobType.map(jt => jt.repairWithParts).orNull
	return {disabled}
}

export default connect(mapStateToProps, mapDispatchToProps)(SelectJobMerchantBranchLocation)